'use client'

import '@/components/agenda/agenda.css'
import { ImageFieldProps } from '@/types/types'
import AppImage from '../AppImage'

export type AgendaDayProps = {
  title?: string
  items: AgendaItemProps[]
}

export type AgendaItemProps = {
  image: ImageFieldProps
  time: string
  disable_height_limit: boolean
  title?: string
  subtitle?: string
  description: string
}

export type AgendaProps = {
  days: AgendaDayProps[]
  download_button: boolean
}

// TODO: Add download button
export default function Agenda({ days, download_button = false }: AgendaProps) {
  if (!days?.length) return null

  return (
    <div className="downloads component no-list-styles">
      <div className="container @container">
        {/* Left/right border */}
        <div className="flex justify-center border-gradient gradient-angle-[to_bottom] @[0px]/single-simple:!border-0 @m-up:border-l @m-up:border-r">
          {/* Inner container */}
          <div className="flex w-full flex-col gap-m @m-up:max-w-[var(--container-s-max-w)]">
            {/* Coloured block */}
            {days.map((day, dayIndex) => {
              return (
                <div
                  key={dayIndex}
                  className="overflow-hidden rounded-lg bg-theme p-s text-fg gradient-angle-[to_bottom] theme--gradient-light @[500px]:p-l theme--gradient-light:bg-none">
                  {/* Days */}
                  <div className="flex flex-col gap-s">
                    <>
                      {day.title ? <h4 className="mb-0 text-center">{day.title}</h4> : null}

                      <div className={`agenda__day flex flex-col`} key={`day-${dayIndex}`}>
                        {day.items.map((item, itemIndex) => (
                          <div className="agenda__item" key={`item-${itemIndex}`}>
                            <div className="agenda__item-dot py-xxs first:pt-0 last:pb-0"></div>
                            <div className="agenda__item-details py-xxs first:pt-0 last:pb-0">
                              <AgendaItem {...item} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const AgendaItem = ({
  title,
  subtitle,
  description,
  image,
  time,
  disable_height_limit = false,
}: AgendaItemProps) => {
  const { url: imageUrl, alt: imageAlt } = image

  // CMS setting to switch between full and accordion display
  let displayType = disable_height_limit ? 'full' : 'accordion'

  // If there's no title, attempt to extract one from the description
  if (!title && !subtitle) {
    const paragraphs = description.split('</p>')
    title = paragraphs[0].replace('<p>', '').trim()
    description = paragraphs.slice(1).join('</p>').trim()
  }

  // If there's no additional content, don't use accordion style
  displayType = (!title && !subtitle) || !description.length ? 'full' : displayType

  // An annoying way to ensure descriptions line up with titles
  const itemGridClass = `@[565px]/agenda-content:grid-cols-[105px,1fr,24px] @[565px]/agenda-content:gap-m grid-cols-[1fr,24px]`

  const titleHtml = (
    <div className={`grid w-full ${itemGridClass}`}>
      {/* Time */}
      <div className="font-bold text-fg-title">{time}</div>

      {/* Title/Subtitle */}
      <div className="row-start-2 flex-grow @[565px]/agenda-content:row-start-auto">
        {title && (
          <strong className="block text-fg-title" dangerouslySetInnerHTML={{ __html: title }} />
        )}
        {subtitle && <span className="block text-fg-subtitle">{subtitle}</span>}
      </div>

      {/* Arrow */}
      {displayType === 'accordion' ? (
        <svg aria-hidden="true" className="ml-auto aspect-square w-[24px] shrink-0 text-fg-title">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-chevron-down`}></use>
        </svg>
      ) : null}
    </div>
  )

  const descriptionHtml = description && (
    <div className={`grid w-full pt-s ${itemGridClass} style-lists`}>
      {/* Time spacer */}
      <div></div>

      {/* Content */}
      <div className="row-start-2 flex-grow @[565px]/agenda-content:row-start-auto">
        <div className="p--small mb-0" dangerouslySetInnerHTML={{ __html: description }} />
      </div>

      {/* Arrow spacer */}
      {displayType === 'accordion' ? <div></div> : null}
    </div>
  )

  return (
    <div className="flex w-full flex-col items-start gap-xxs overflow-hidden rounded-md bg-theme p-s text-[length:--p-size] text-fg @container/agenda-item theme--base-0 @[400px]:flex-row @[400px]:gap-m">
      {/* Image */}
      {imageUrl && (
        <div className="w-[70px] flex-shrink-0 overflow-hidden rounded-sm @[500px]/agenda-item:w-[100px]">
          <AppImage
            path={imageUrl}
            alt={imageAlt}
            transforms={{ height: '200', width: '200' }}
            width={200}
            height={200}
            className="w-full"
          />
        </div>
      )}

      {/* Title/Content */}
      <div className="w-full @container/agenda-content">
        {displayType === 'accordion' ? (
          <details className="w-full [&_svg]:open:rotate-180">
            <summary className="w-full cursor-pointer list-none appearance-none [&::-webkit-details-marker]:hidden">
              {titleHtml}
            </summary>
            {descriptionHtml}
          </details>
        ) : (
          <>
            {titleHtml}
            {descriptionHtml}
          </>
        )}
      </div>
    </div>
  )
}
